import { omit } from 'lodash-es';
import React, { useRef } from 'react';
import { Transition } from 'react-transition-group';
import { getDisplayName } from 'utils/react';
import {
  LOADER_TRANSITION_IN_DEFAULT_DELAY_MILLIS,
  LOADER_TRANSITION_IN_DURATION_MILLIS,
} from './constants';
import * as S from './styles';
import { AnimatedDelayedLoaderStyleProps, DelayedLoaderProps } from './types';

/**
 * A higher-order component which adds the ability to fade-in a loader after a certain
 * delay. This is useful to prevent loaders from displaying when loading times are
 * very short
 *
 * This HOC was designed for loaders from react-spinners, which does not forward
 * refs for any of the spinners.  The transition-in requires a ref, so the loaders
 * are wrapped in another element and that element's ref is used.  Note that there
 * could be issues with which props wind up on the wrapper and which wind up on the
 * loader itself.
 */
export default function createDelayedLoader<TProps>(
  Component: React.ComponentType<TProps>,
) {
  const DelayedLoader: React.FC<DelayedLoaderProps<TProps>> = (props) => {
    const { className, loaderClassName, ...restProps } = props;

    const nodeRef = useRef<HTMLSpanElement>(null);

    const element: React.ReactElement<
      AnimatedDelayedLoaderStyleProps & React.RefAttributes<HTMLSpanElement>
    > = (
      <S.Container className={className}>
        <Component
          className={loaderClassName}
          {...(omit(restProps, 'fadeIn', 'delayMillis') as TProps)}
        />
      </S.Container>
    );

    if (!props.fadeIn) {
      return element;
    }
    const { delayMillis = LOADER_TRANSITION_IN_DEFAULT_DELAY_MILLIS } = props;

    return (
      <Transition
        appear
        in
        mountOnEnter
        nodeRef={nodeRef}
        timeout={{ enter: delayMillis + LOADER_TRANSITION_IN_DURATION_MILLIS }}
      >
        {(status) =>
          React.cloneElement(element, {
            $animate: true,
            $delayMillis: delayMillis,
            $transitionStatus: status,
            ref: nodeRef,
          })
        }
      </Transition>
    );
  };

  DelayedLoader.displayName = `delayed(${getDisplayName(Component)})`;

  return DelayedLoader;
}
